import { z } from 'zod';
import { DateSchema } from './entities/base.schema';
import { ProcessedImageSchema } from './entities/shared.schema';
import {
  EntitiesMetaSchema,
  NarrativeMetaSchema,
  NovelStorageLayoutSchema
} from './entities/summary.schema';

/**
 * Current status of the novel.
 */
export const NovelStatusEnum = z.enum(['draft', 'in-progress', 'published']);

/**
 * Output language for generated novel content.
 */
export const NovelLanguageEnum = z.enum([
  'English',
  'Spanish',
  'Catalan',
  'French',
  'Italian',
  'German',
]);
export type NovelLanguage = z.infer<typeof NovelLanguageEnum>;

/**
 * Size classification of the novel.
 */
export const NovelSizeEnum = z.enum(['small', 'medium', 'large']);

/**
 * Selected image style for AI generation.
 */
export const NovelImageStyleEnum = z.enum(['style1', 'style2', 'style3', 'style4', 'style5', 'style6', 'custom']);

/**
 * Intelligence tier for AI model selection.
 */
export const IntelligenceTierEnum = z.enum(['eco', 'balanced', 'premium']);
export type IntelligenceTier = z.infer<typeof IntelligenceTierEnum>;

/**
 * Novel schema.
 * Master source for Novel entity structure.
 */
export const NovelSchema = z.object({
  id: z.string().describe('Unique identifier for the Novel entity.'),
  publicationId: z.string().optional().describe('Backend-generated stable public publication identifier.'),
  title: z.string().describe('The title of the novel.'),
  ownerId: z.string().describe('The UID of the user who owns the novel.'),
  genre: z.string().optional().describe('Genre of the novel.'),
  status: NovelStatusEnum.default('draft').describe('Current status of the novel.'),
  wordCount: z.number().optional().describe('Total word count (updated via Cloud Function).'),
  lastEditedAt: z.string().datetime().optional().describe('When the novel was last edited.'),
  coverImageUrl: z.string().url().optional().describe('URL to the novel\'s cover image.'),
  coverImage: ProcessedImageSchema.optional().describe('Processed book cover image variants.'),
  description: z.string().optional().describe('Short description for library views.'),
  structureTemplateId: z.string().optional().describe('ID of the selected narrative structure template.'),
  imageStyle: NovelImageStyleEnum.optional().describe('Selected image style for AI generation.'),
  customStylePrompt: z.string().optional().describe('User-defined custom style description.'),
  customStylePreviewUrl: z.string().url().optional().describe('URL to the custom style preview image.'),
  customStylePreviewPath: z.string().optional().describe('Storage path to the custom style preview image.'),
  language: NovelLanguageEnum.optional().describe('Output language for generated novel content.'),
  theme: z.string().optional().describe('Primary thematic element of the novel (e.g., drama, fantasy).'),
  audience: z.string().optional().describe('Target audience for the novel (e.g., adult, young adult).'),
  worldSetting: z.string().optional().describe('The setting or world of the novel (e.g., ancient world, sci-fi).'),
  size: NovelSizeEnum.default('small').describe('The size classification of the novel.'),
  collaborators: z.record(z.string(), z.string()).optional().default({}).describe('Map of user IDs to display names for collaborators.'),
  readers: z.record(z.string(), z.string()).optional().default({}).describe('Map of user IDs to display names for readers.'),
  noteFolders: z.record(z.string()).optional().default({}).describe('Map of folder IDs to folder names for research notes.'),
  noteFolderParents: z.record(z.string()).optional().describe('Optional map of note folder IDs to their parent folder IDs. Missing entries mean root folders.'),
  relationshipsHiddenEntityTypeIds: z.array(z.string()).optional().describe('Entity type filters hidden in the Relationships view.'),
  entityListSortDefault: z.enum(['updatedAt', 'name']).optional().describe('Default ordering for entity lists.'),
  entityListSortBy: z.record(z.enum(['updatedAt', 'name'])).optional().describe('Per-entity or per-Codex-category ordering overrides.'),
  pendingCollaborators: z.array(z.string()).optional().describe('Array of email addresses who have pending collaborator invites.'),
  pendingReaders: z.array(z.string()).optional().describe('Array of email addresses who have pending reader invites.'),

  // Storage Layout Configuration
  storageLayout: NovelStorageLayoutSchema.describe('Defines where each data block is stored.'),

  // Metadata (Embedded lists)
  entitiesMeta: EntitiesMetaSchema.optional().default({}).describe('Embedded worldbuilding metadata.'),
  narrativeMeta: NarrativeMetaSchema.optional().default({}).describe('Embedded narrative metadata.'),



  // AI/Generation Metadata
  canonRevision: z.string().optional().default('1').describe('Version of the canon used for generation.'),
  tone: z.string().optional().describe('Overall tone of the novel for AI agents.'),
  glossary: z.record(z.string()).optional().describe('Key terms and their definitions unique to this novel.'),

  // AI settings
  aiSettings: z.object({
    intelligenceTier: IntelligenceTierEnum.default('eco').describe('The intelligence tier for AI model selection.'),
  }).optional().default({ intelligenceTier: 'eco' }).describe('AI generation settings for this novel.'),

  // Example novel flag — set on ePlotera-owned demo novels
  isExample: z.boolean().optional()
    .describe('If true, this novel is a read-only example shown to all authenticated users.'),

  // Migration State
  migration: z.object({
    inProgress: z.boolean().default(false),
    paths: z.array(z.object({
      type: z.string(),
      family: z.enum(['entities', 'narrative']),
      block: z.enum(['items', 'meta']),
    })).default([]),
    startedAt: DateSchema.optional(),
    completedAt: DateSchema.optional(),
    failedAt: DateSchema.optional(),
    error: z.string().optional(),
  }).optional(),
});

export type Novel = z.infer<typeof NovelSchema>;
